import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChange } from '../firebase/auth';
import { getOriginalUserId } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);
      setLoading(false);
      
      // Expose user globally for getUserId() function
      if (typeof window !== 'undefined') {
        window.__authContext = { user, isAuthenticated: !!user };
      }

      // On login, auto-register with backend and link anonymous files from this device
      (async () => {
        try {
          if (user) {
            const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
            // UNIFIED SYSTEM: Always use the original UUID, NOT Firebase UID
            const currentUserId = localStorage.getItem('fileshare_user_id');
            
            console.log('🔐 User logged in with email:', user.email);
            console.log('🆔 Keeping original UUID:', currentUserId);
            console.log('🔑 Firebase UID (for auth only):', user.uid);
            
            // 1) Register/update user in backend (Firebase UID for auth, UUID for identity)
            await fetch(`${apiBase}/api/user/register`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: user.email,
                firebaseUid: user.uid,
                displayName: user.displayName || null,
                anonymousUserId: currentUserId  // Send the UUID, not Firebase UID
              })
            }).catch(() => {});

            // 2) Link anonymous files (if any other devices used this email before)
            await fetch(`${apiBase}/api/user/link-anonymous`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                userId: currentUserId,  // Use UUID, not Firebase UID
                anonymousUserId: currentUserId 
              })
            }).catch(() => {});
            
            console.log('✅ User registration complete, UUID unchanged:', currentUserId);
          }
        } catch (_) {}
      })();
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};


